import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import useBaseRequest from "../../api/BaseRequest";
import DocumentGateway from "../../api/gateways/DocumentGateway";
import { useToast } from "../../components/toast";
import { DocumentActionsTypes, IDocument, DocumentStatus } from "../../entities/IDocument";
import { IMetaDataRule, MetaData, MetaDataForm } from "../../entities/IMetaData";
import { useWindowDimensions } from "../../modules/window";
import { useDocumentService } from "../../services/useDocumentService";
import { screens } from "../Navigation.types";
import OptionListGateway from "../../api/gateways/OptionListGateway";
import { useUser } from "../../store/auth/AuthSelectors";
import { useTranslation } from "react-i18next";
import { IOption } from "../../components/select";
import LabelGateway from "../../api/gateways/LabelGateway";
import DigitalSignatureGateway from "../../api/gateways/DigitalSignatureGateway";
import DigitalSignatureQualifiedGateway from "../../api/gateways/DigitalSignatureQualifiedGateway";
import { requestDigitalSignatureForm as requestDigitalSignatureFormDefault } from "../../components/request-digital-signature";
import { requestQdsForm as requestQdsFormDefault } from "../../components/requst-qds-signature/RequestQdsModal.types";
import { GenericForm } from "../../components/generic-form/GenericForm.types";
import { EditViewDigitalSignatureRequestDTO } from "../../components/request-digital-signature/ViewDigitalSignatureModal.types";
import { QdsRequestDTO, QdsStatusEnum } from "../../entities/IQdsRequest";
import moment from "moment";

export function useDocument(id: string) {
  const { t } = useTranslation();
  const [metaDataRules, setMetaDataRules] = React.useState<IMetaDataRule[]>([]);
  const [metaData, setMetaData] = React.useState<MetaData>({});
  const [docFile, setDocFile] = React.useState<{
    uri: string;
    type: string;
    name: string;
    file: File | null;
    edited: boolean;
  } | null>(null);
  const [labels, setLabels] = React.useState<IOption<number>[]>([]);
  const [availableTags, setAvailableTags] = React.useState<string[]>([]);
  const [tags, setTags] = React.useState<string[]>([]);
  const [loadingTags, setLoadingTags] = React.useState(false);
  const [explicitAccessEmails, setExplicitAccessEmails] = React.useState<string[]>([]);

  // states
  const [editing, setEditing] = React.useState(false);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const [showDismissModal, setShowDismissModal] = React.useState(false);
  const navigate = useNavigate();
  const { success, danger } = useToast();
  const user = useUser();

  // requests
  const { mapMetaDataRulesToForm, getDocFileUrl } = useDocumentService();

  const {
    data,
    loading,
    execute: fetch,
  } = useBaseRequest(DocumentGateway.getDocumentByIdAndMetaData, {
    initialPayload: { id },
    autoFetch: true,
    onCompleted: (data) => {
      if (!data) return;
      if (data.document.metaData) {
        setMetaData(data.document.metaData);
      }
      if (data.metaDataRules) {
        setMetaDataRules(data.metaDataRules);
      }
      if (data.document.labels) {
        setLabels(
          data.document.labels.map((label) => ({
            label: label.name,
            value: label.id,
            color: label.color,
          }))
        );
      }
      if (data.document.tags) {
        setTags(data.document.tags);
      }
      // Handle DocumentExplicitAccess (API returns with capital D)
      const explicitAccess = (data.document as any).DocumentExplicitAccess || data.document.documentExplicitAccess;
      if (explicitAccess && Array.isArray(explicitAccess)) {
        setExplicitAccessEmails(explicitAccess.map(ua => ua.email));
      }
      setDefaultFile(data.document);
    },
  });

  const { execute: update, loading: updating } = useBaseRequest(DocumentGateway.updateDocument, {
    onCompleted: (data, payload) => {
      fetch({ id: payload.id });
      setEditing(false);
      success(t("DOCUMENTS.UPDATED_MESSAGE"));
      onClearAction();
    },
  });

  const { execute: dismiss } = useBaseRequest(DocumentGateway.dismissDocument, {
    onCompleted: (data) => {
      // refreshTaskCounters();
      closeModal();
      navigate(screens.documents);
      success(t("DOCUMENTS.DELETED_MESSAGE"));
    },
  });

  // handlers
  const onEditCancel = () => {
    setEditing(false);
    setDefaultFile(data?.document);
  };

  const setDefaultFile = (doc?: IDocument) => {
    const file = doc?.files && doc.files.length ? doc.files[doc.files.length - 1] : null;
    if (!file) {
      setDocFile(null);
      return;
    }
    const fileUrl = getDocFileUrl(file.url);
    setDocFile({
      uri: fileUrl,
      type: file.mimeType || "",
      name: file.name,
      file: null,
      edited: false,
    });
  };

  const onFileEditCancel = () => {
    setDefaultFile(data?.document);
  };
  const onFileEdit = (newFile: File) => {
    setDocFile({
      edited: true,
      type: newFile.type,
      name: newFile.name,
      file: newFile,
      uri: URL.createObjectURL(newFile),
    });
  };

  const onUpdateDoc = (metaData: MetaDataForm) => {
    const params: any = {
      id: data?.document?.id?.toString(),
      metaData,
      tags,
      explicitAccessEmails,
    };

    if (!!docFile && docFile.file && docFile.edited) {
      params.file = docFile.file;
      params.status = DocumentStatus.PENDING;
    }

    const findNewLabels = labels
      .filter((item) => !data?.document.labels?.find((label) => label.id === item.value))
      .map((item) => item.value);
    const findDeletedLabels = data?.document.labels
      ?.filter((item) => !labels.find((label) => label.value === item.id))
      .map((item) => item.id);

    if (findNewLabels.length && data?.document.id) {
      addLabelsToElement({ id: data?.document.id, labelIds: findNewLabels, type: "document" });
    }
    if (data?.document.id && findDeletedLabels && findDeletedLabels.length) {
      removeLabelsFromElement({
        id: data?.document.id,
        labelIds: findDeletedLabels,
        type: "document",
      });
    }
    update(params);
  };

  const closeModal = () => setShowDismissModal(false);
  const openModal = () => setShowDismissModal(true);

  const onDismiss = () => {
    dismiss({ id: data?.document.id?.toString() || "" });
  };

  const updateLabels = (labels: IOption<number>[]) => {
    setLabels(labels);
  };

  const updateTags = (tags: string[]) => {
    setTags(tags);
  };

  // Load available tags on component mount
  React.useEffect(() => {
    const loadTags = async () => {
      try {
        setLoadingTags(true);
        const response = await DocumentGateway.getDocumentTags();
        setAvailableTags(response.available);
      } catch (error) {
        danger(t("ERRORS.FAILED_TO_LOAD_TAGS", "Failed to load tags"));
      } finally {
        setLoadingTags(false);
      }
    };
    loadTags();
  }, []);

  const { execute: addLabelsToElement } = useBaseRequest(
    LabelGateway.addLabelsToElement,
    {
      onCompleted: () => {},
      onError: (error) => {
        danger(error?.message || t("ERRORS.UNKNOWN"));
      },
    }
  );
  const { execute: removeLabelsFromElement } = useBaseRequest(
    LabelGateway.removeLabelsFromElement,
    {
      onCompleted: () => {},
      onError: (error) => {
        danger(error?.message || t("ERRORS.UNKNOWN"));
      },
    }
  );

  //Digital Signature
  const requestDigitalSignatureForm = useMemo(() => {
    return {
      ...requestDigitalSignatureFormDefault,
      documentId: {
        ...requestDigitalSignatureFormDefault.documentId,
        defaultValue: data?.document.id || -1,
      },
    };
  }, [data?.document]);

  //Qualified Digital Signature
  const requestQdsForm = useMemo(() => {
    return {
      ...requestQdsFormDefault,
      documentId: {
        ...requestQdsFormDefault.documentId,
        defaultValue: data?.document.id || -1,
      },
    };
  }, [data?.document]);

  const { execute: requestDigitalSignature, loading: requestingDigitalSignature } = useBaseRequest(
    DigitalSignatureGateway.createDigitalSignatureRequest,
    {
      onCompleted: (data) => {
        fetch({ id });
        success(t("DOCUMENTS.REQUEST_SUCCESS"));
        onClearAction();
      },
      onError: (error) => {
        danger(error?.message || t("ERRORS.UNKNOWN"));
      },
    }
  );

  const { execute: requestQds, loading: requestingQds } = useBaseRequest(
    DigitalSignatureQualifiedGateway.createQdsRequest,
    {
      onCompleted: (data) => {
        fetch({ id });
        success(t("DOCUMENTS.REQUEST_SUCCESS"));
        onClearAction();
      },
      onError: (error) => {
        danger(error?.message || t("ERRORS.UNKNOWN"));
      },
    }
  );

  const onRequestQds = (formData: any) => {
    const splitEmails = (value: any) => {
      if (Array.isArray(value)) {
        return value.filter((email: string) => email && typeof email === 'string');
      }
      if (typeof value === 'string') {
        return value
          .split(",")
          .map((email: string) => email.trim())
          .filter((email: string) => email);
      }
      return [];
    };

    const payload: QdsRequestDTO = {
      documentId: formData.documentId,
      consentEmails: splitEmails(formData.consentEmails),
      holderSignerEmails: splitEmails(formData.holderSignerEmails),
      counterPartyEmails: splitEmails(formData.counterPartyEmails),
    };
    requestQds(payload);
  };

  const digitalSignatureFields = useMemo(() => {
    if (!data?.document.digitalSignRequest) return null;
    const fields = data?.document.digitalSignRequest;
    const form: GenericForm<MetaDataForm> | null = {
      email: {
        label: "Customer Email",
        defaultValue: fields.email,
        type: "input",
        name: "",
      },
      status: {
        label: "Status",
        defaultValue: fields.status[0].toUpperCase() + fields.status.slice(1),
        type: "paragraph",
        name: "",
        disabledIf: (value) => true,
      },
      authorizedSignatory: {
        label: "Authorized Signatory",
        defaultValue: Number(fields.authorizedSignatoryId),
        type: "autocompleteSingleChoice",
        name: "",
      },
      confirmedAt: {
        label: "Confirmed At",
        defaultValue: fields.confirmedAt
          ? moment(fields.confirmedAt).format("DD/MM/YYYY HH:mm:ss")
          : "",
        type: "paragraph",
        name: "",
        disabledIf: (value) => true,
      },
      signedAt: {
        label: "Signed At",
        defaultValue: fields.signedAt ? moment(fields.signedAt).format("DD/MM/YYYY HH:mm:ss") : "",
        type: "paragraph",
        name: "",
        disabledIf: (value) => true,
      },
      sendEmailToCustomer: {
        label: "DOCUMENTS.SEND_EMAIL_TO_CUSTOMER",
        defaultValue: false,
        type: "checkbox",
        name: "sendEmailToCustomer",
        validators: [],
      },
    };
    return form;
  }, [data?.document.digitalSignRequest]);

  const [selectedAction, setSelectedAction] = useState<string | undefined>();
  const onActionSelected = (action: string) => {
    const task = data?.document.task;
    if (action === DocumentActionsTypes.VIEW_TASK && task) {
      navigate(screens.task.root.replace(":id", task.id.toString()));
      return;
    }

    if (action === DocumentActionsTypes.RESEND_DOCUMENT) {
      onResendDigitalSignature();
    }

    setSelectedAction(action);
  };
  const onClearAction = () => {
    onEditCancel();
    setSelectedAction(undefined);
  };
  const { execute: deleteDigitalSignatureRequest } = useBaseRequest(
    DigitalSignatureGateway.deleteDigitalSignatureRequest,
    {
      onCompleted: (data) => {
        success(t("DOCUMENTS.DELETE_SIGNATURE_SUCCESS"));
        closeDeleteDigitalSignatureModal(true);
        fetch({ id });
        setShowDismissModal(false);
      },
    }
  );
  const [showDeleteSignatureModal, setShowDeletesignatureModal] = useState(false);

  const closeDeleteDigitalSignatureModal = (dontShowDigitalSignature?: boolean) => {
    if (dontShowDigitalSignature === undefined || !dontShowDigitalSignature) {
      setSelectedAction(DocumentActionsTypes.VIEW_DIGITAL_SIGNATURE);
    }
    setShowDeletesignatureModal(false);
  };
  const onStartDeleteDigitalSignature = () => {
    setShowDeletesignatureModal(true);
    setSelectedAction(undefined);
  };
  const onDeleteDigitalSignature = () => {
    data?.document.digitalSignRequest?.id &&
      deleteDigitalSignatureRequest(data?.document.digitalSignRequest?.id);
  };

  const { execute: editDigitalSignatureRequest, loading: editingSignature } = useBaseRequest(
    ({ id, data }: { id: string; data: EditViewDigitalSignatureRequestDTO }) => {
      return DigitalSignatureGateway.editDigitalSignatureRequest(id, data);
    },
    {
      onCompleted: (data) => {
        success(t("DOCUMENTS.EDIT_SIGNATURE_SUCCESS"));
        fetch({ id });
        onClearAction();
      },
    }
  );

  const handleEdit = (payload: EditViewDigitalSignatureRequestDTO) => {
    if (!data?.document?.digitalSignRequest?.id) return;

    editDigitalSignatureRequest({
      id: String(data?.document?.digitalSignRequest?.id),
      data: payload,
    });
  };

  const { execute: resendSignatureRequest } = useBaseRequest(
    ({
      documentId,
      data,
    }: {
      documentId: string;
      data: { email: string; authorizedSignatory: number };
    }) => {
      return DigitalSignatureGateway.resendDocumentForDigitalSignature(documentId, data);
    },
    {
      onCompleted: () => {
        success(t("DOCUMENTS.RESEND_SIGNATURE_SUCCESS"));
        onClearAction();
      },
      onError: (error) => {
        danger(error?.message || t("ERRORS.UNKNOWN"));
      },
    }
  );

  const onResendDigitalSignature = () => {
    if (!data?.document?.digitalSignRequest?.id) return;

    resendSignatureRequest({
      documentId: String(data.document.id),
      data: {
        email: data.document.digitalSignRequest.email,
        authorizedSignatory: Number(data.document.digitalSignRequest.authorizedSignatoryId) || 0,
      },
    });
  };

  return {
    document: data?.document,
    metaDataForm: (() => {
      const form = mapMetaDataRulesToForm(metaDataRules, metaData);
      if (
        metaData.documentStatus === "Signed" &&
        data?.document?.qdsRequest?.status === QdsStatusEnum.SIGNED &&
        form.documentStatus
      ) {
        form.documentStatus = { ...form.documentStatus, disabledIf: () => true };
      }
      return form;
    })(),
    loading,
    windowHeight,
    windowWidth,
    onUpdateDoc,
    editing,
    docFile,
    onFileEdit,
    onFileEditCancel,
    closeModal,
    openModal,
    onDismiss,
    updating,
    user,
    labels: labels || [],
    updateLabels,
    previewLabels: data?.document.labels || [],
    onRequestDigitalSignature: requestDigitalSignature,
    requestingDigitalSignature,
    requestDigitalSignatureForm,
    digitalSignatureFields,
    onRequestQds,
    requestingQds,
    requestQdsForm,
    onActionSelected,
    onClearAction,
    selectedAction,
    onStartDeleteDigitalSignature,
    onDeleteDigitalSignature,
    showDeleteSignatureModal,
    closeDeleteDigitalSignatureModal,
    handleEdit,
    editingSignature,
    tags,
    availableTags,
    updateTags,
    loadingTags,
    explicitAccessEmails,
    updateExplicitAccessEmails: setExplicitAccessEmails,
  };
}
